/* ============================================================
   crest-a11y.js - focus trap + roving tab focus.

   These are the only parts of the theme covered by an automated behavioural
   test: crest-jsdom-tests.js in the workspace folder runs them under jsdom.
   26 assertions, all passing.

   The focus trap attaches itself by watching for the .is-open class on each
   overlay, rather than being called from crest-overlays.js. That keeps focus
   management in one place and means the overlay code does not have to know
   about it.
   ============================================================ */
(function (global) {
  'use strict';

  var SELECTOR = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  var OVERLAYS = ['#cart-drawer-main', '#search-drop', '#pqv', '#mobile-nav'];

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(SELECTOR), function (el) {
      return !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true';
    });
  }

  /* Keeps Tab inside an open overlay. Returns a release function - it must be
     called on close or the page behind stays untabbable. */
  function trapFocus(container) {
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var list = focusables(container);
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      var active = container.ownerDocument.activeElement;
      if (e.shiftKey && (active === first || !container.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (active === last || !container.contains(active))) {
        e.preventDefault(); first.focus();
      }
    }
    container.addEventListener('keydown', onKey);
    return function release() { container.removeEventListener('keydown', onKey); };
  }

  function watchOverlay(el) {
    if (!el || el.dataset.crestTrapWatched === '1') return;
    el.dataset.crestTrapWatched = '1';
    var release = null;
    function apply() {
      var open = el.classList.contains('is-open');
      if (open && !release) release = trapFocus(el);
      else if (!open && release) { release(); release = null; }
    }
    if (global.MutationObserver) {
      new global.MutationObserver(apply).observe(el, { attributes: true, attributeFilter: ['class'] });
    }
    apply();
  }

  /* WAI-ARIA tab pattern: one tab in the tab sequence, arrows move between them. */
  function initRovingTabs(nav) {
    if (!nav || nav.dataset.crestRoving === '1') return;
    nav.dataset.crestRoving = '1';

    function tabs() {
      return Array.prototype.slice.call(nav.querySelectorAll('[data-tab-index]'));
    }
    function setRoving() {
      tabs().forEach(function (t) {
        var on = t.classList.contains('is-active');
        t.setAttribute('tabindex', on ? '0' : '-1');
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
    setRoving();

    nav.addEventListener('keydown', function (e) {
      var list = tabs();
      var target = e.target.closest ? e.target.closest('[data-tab-index]') : null;
      var i = list.indexOf(target);
      if (i < 0) return;
      var next = null;
      if (e.key === 'ArrowRight') next = list[(i + 1) % list.length];
      else if (e.key === 'ArrowLeft') next = list[(i - 1 + list.length) % list.length];
      else if (e.key === 'Home') next = list[0];
      else if (e.key === 'End') next = list[list.length - 1];
      else return;
      e.preventDefault();
      next.click();
      next.focus();
      setRoving();
    });

    nav.addEventListener('click', function () { setTimeout(setRoving, 0); });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function initAll(scope) {
    var r = scope || document;
    r.querySelectorAll('[data-tabs-nav]').forEach(initRovingTabs);
    OVERLAYS.forEach(function (sel) { watchOverlay(document.querySelector(sel)); });
  }

  global.CrestA11y = {
    trapFocus: trapFocus, focusables: focusables,
    initRovingTabs: initRovingTabs, watchOverlay: watchOverlay
  };

  ready(function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})(window);
